import { ReactNode } from 'react'
import { WorkflowRunStepState } from '@digital-twins/shared-types'

import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { formatThreadRelativeTime } from '@/pages/ThreadsPage/threadHelpers'

import { runPaneStateLabel } from './RunStepRow'

export interface ThreadStepGroupProps {
  title: string
  state: WorkflowRunStepState
  /**
   * Step clock for the relative-time label. Pass `completedAt`, else
   * `startedAt`. Omitted or invalid values hide the time.
   */
  at?: string | null
  /**
   * Step row id, rendered as `data-step-id`. The thread scrolls a step's group
   * into view when that step settles mid-run, and this is how it finds it.
   */
  stepId?: string | null
  children?: ReactNode
  className?: string
}

/**
 * One step's output group: header (title, state, relative time) + children.
 * Step 4b mounts this from ThreadInlineArtifacts; this file does not group.
 */
export function ThreadStepGroup(props: ThreadStepGroupProps) {
  const { title, state, at, stepId, children, className } = props

  const stateLabel = runPaneStateLabel(state)
  const relativeTime = formatThreadRelativeTime(at)

  const timeLabel = relativeTime ? (
    <Text
      as="span"
      size="xs"
      color="muted"
      data-testid="thread-step-group-time"
    >
      {relativeTime}
    </Text>
  ) : null

  return (
    <section
      className={cn('space-y-3', className)}
      data-testid="thread-step-group"
      data-state={state}
      data-step-id={stepId ?? undefined}
    >
      <header
        className="flex items-baseline justify-between gap-2"
        data-testid="thread-step-group-header"
      >
        <Heading level={3} size="sm" weight="medium" className="min-w-0 truncate">
          {title}
        </Heading>
        <Text
          as="span"
          size="xs"
          color="muted"
          className="shrink-0"
          data-testid="thread-step-group-state"
        >
          {stateLabel}
          {timeLabel ? ' · ' : null}
          {timeLabel}
        </Text>
      </header>
      {children}
    </section>
  )
}
